import React from 'react';
import { Check, DeleteOutline } from '@mui/icons-material';
import { Colors } from 'library';
import { Card, Chip, IconButton, Row, Stack, Text, TextButton } from 'library/ui';
import { PaymentMethodBadge } from './PaymentMethodBadge';
import { PaymentMethod, isBankAccount, isCreditCard } from './types';

interface PaymentMethodRowProps {
    method: PaymentMethod;
    showSubsDefault: boolean;
    showRecruitDefault: boolean;
    recruitDefaultLabel?: string;
    setRecruitDefaultLabel?: string;
    onSetSubsDefault: () => void;
    onSetRecruitDefault: () => void;
    onDelete: () => void;
}

export function PaymentMethodRow({
    method,
    showSubsDefault,
    showRecruitDefault,
    recruitDefaultLabel = 'Recruit Default',
    setRecruitDefaultLabel = 'Set Recruit Default',
    onSetSubsDefault,
    onSetRecruitDefault,
    onDelete,
}: PaymentMethodRowProps) {
    const isBank = isBankAccount(method);
    const isCard = isCreditCard(method);

    const primaryLabel = isBank
        ? `${method.bank_name || 'Bank account'} •••• ${method.last4 || '????'}`
        : `•••• •••• •••• ${method.last4 || '????'}`;

    const secondaryLabel = isCard
        ? `${method.cardholder_name ? `${method.cardholder_name} · ` : ''}Expires ${String(method.exp_month ?? '').padStart(2, '0')}/${method.exp_year ?? ''}`
        : method.cardholder_name ?? '';

    return (
        <Card padding="md">
            <Row justify="between" align="center">
                <Row gap="md" align="center">
                    <PaymentMethodBadge method={method} />
                    <Stack gap="none">
                        <Text weight="semibold">{primaryLabel}</Text>
                        {secondaryLabel && (
                            <Text size="sm" tone="secondary">
                                {secondaryLabel}
                            </Text>
                        )}
                    </Stack>
                </Row>
                <Row gap="md" align="center">
                    {showSubsDefault && method.is_default && <DefaultChip label="Default" />}
                    {showRecruitDefault && method.is_recruit_default && <DefaultChip label={recruitDefaultLabel} />}
                    {showSubsDefault && !method.is_default && (
                        <TextButton onClick={onSetSubsDefault} color={Colors.mediumGrey}>
                            Set Default
                        </TextButton>
                    )}
                    {showRecruitDefault && !method.is_recruit_default && isCard && (
                        <TextButton onClick={onSetRecruitDefault} color={Colors.mediumGrey}>
                            {setRecruitDefaultLabel}
                        </TextButton>
                    )}
                    <IconButton icon={DeleteOutline} onClick={onDelete} />
                </Row>
            </Row>
        </Card>
    );
}

function DefaultChip({ label }: { label: string }) {
    return (
        <Chip variant="outlined" bgColor={Colors.fill} textColor={Colors.darkNavy} borderColor={Colors.lightGrey}>
            <Check sx={{ fontSize: 14 }} />
            {label}
        </Chip>
    );
}
